<template>
  <div class="scenario-requirements">
    <!-- Word Problem -->
    <div class="card mb-3 scenario-problem-card">
      <div
        class="card-header fw-bold d-flex align-items-center gap-2 scenario-problem-header"
      >
        <i class="bi bi-book-half scenario-problem-icon" aria-hidden="true"></i>
        <span class="scenario-problem-title">Scenario Problem</span>
      </div>
      <div class="card-body scenario-problem-body">
        <p class="word-problem mb-0 text-start">{{ scenario.wordProblem }}</p>
      </div>
    </div>

    <!-- Requirements Entry Form -->
    <div class="card">
      <div class="card-header fw-bold d-flex align-items-center gap-2">
        <i class="bi bi-pencil-square" aria-hidden="true"></i>
        <span>Enter Your Requirements</span>
      </div>
      <div class="card-body">
        <p class="text-muted small mb-3">
          Read the scenario above carefully, then identify and enter each
          requirement below.
        </p>

        <div class="row g-3">
          <!-- Fluid Type -->
          <div class="col-md-6">
            <label for="req-fluid-type" class="form-label fluid-type-label">
              <i class="bi bi-droplet-fill me-1" aria-hidden="true"></i>
              Fluid Type
            </label>
            <select
              id="req-fluid-type"
              class="form-select"
              :class="fieldClass('fluidType')"
              v-model="answers.fluidType"
            >
              <option value="">— Select fluid —</option>
              <option v-for="f in fluidOptions" :key="f" :value="f">
                {{ f }}
              </option>
            </select>
            <div class="invalid-feedback">
              That's not the correct fluid — re-read the scenario.
            </div>
            <div class="valid-feedback">Correct!</div>
          </div>

          <!-- Target Flow Rate -->
          <div class="col-md-6">
            <label for="req-flow-rate" class="form-label fw-semibold"
              >Target Flow Rate (GPM)</label
            >
            <input
              id="req-flow-rate"
              type="number"
              class="form-control"
              :class="fieldClass('flowRate')"
              v-model.number="answers.flowRate"
              placeholder="e.g. 50"
              min="0"
            />
            <div class="invalid-feedback">
              Check the flow rate in the scenario.
            </div>
            <div class="valid-feedback">Correct!</div>
          </div>

          <!-- Elevation Change -->
          <div class="col-md-6">
            <label for="req-elevation" class="form-label fw-semibold"
              >Elevation Change (ft)</label
            >
            <input
              id="req-elevation"
              type="number"
              class="form-control"
              :class="fieldClass('elevation')"
              v-model.number="answers.elevation"
              placeholder="e.g. 16.4"
              step="0.1"
              min="0"
            />
            <div class="invalid-feedback">
              Check the elevation value in the scenario.
            </div>
            <div class="valid-feedback">Correct!</div>
          </div>

          <!-- Pipe Run Length -->
          <div class="col-md-6">
            <label for="req-pipe-length" class="form-label fw-semibold"
              >Total Pipe Run Length (ft)</label
            >
            <input
              id="req-pipe-length"
              type="number"
              class="form-control"
              :class="fieldClass('pipeRunLength')"
              v-model.number="answers.pipeRunLength"
              placeholder="e.g. 98.4"
              step="0.1"
              min="0"
            />
            <div class="invalid-feedback">
              Check the pipe run length in the scenario.
            </div>
            <div class="valid-feedback">Correct!</div>
            <output
              v-if="pipeLengthHint && answers.pipeRunLength !== ''"
              class="form-text fw-semibold"
              :class="
                pipeLengthHint === 'short' ? 'text-warning' : 'text-danger'
              "
              aria-live="polite"
            >
              <i
                class="bi me-1"
                :class="
                  pipeLengthHint === 'short'
                    ? 'bi-arrow-down-circle'
                    : 'bi-arrow-up-circle'
                "
                aria-hidden="true"
              ></i>
              {{
                pipeLengthHint === "short"
                  ? "Too short — your value is below the required pipe run length."
                  : "Too long — your value exceeds the required pipe run length."
              }}
            </output>
          </div>

          <!-- Budget -->
          <div class="col-md-6">
            <label for="req-budget" class="form-label fw-semibold"
              >Budget ($)</label
            >
            <input
              id="req-budget"
              type="number"
              class="form-control"
              :class="fieldClass('budget')"
              v-model.number="answers.budget"
              placeholder="e.g. 5000"
              min="0"
            />
            <div class="invalid-feedback">
              Check the budget in the scenario.
            </div>
            <div class="valid-feedback">Correct!</div>
          </div>

          <!-- Constraints -->
          <div class="col-md-6">
            <label for="req-constraints" class="form-label fw-semibold"
              >Design Constraints</label
            >
            <select
              id="req-constraints"
              class="form-select"
              :class="fieldClass('constraints')"
              v-model="answers.constraints"
            >
              <option value="">— Select constraints —</option>
              <option
                v-for="c in scenario.constraintOptions"
                :key="c"
                :value="c"
              >
                {{ c }}
              </option>
            </select>
            <div class="invalid-feedback">
              Those aren't the right constraints — re-read the scenario.
            </div>
            <div class="valid-feedback">Correct!</div>
          </div>
        </div>

        <!-- Actions / Feedback -->
        <div class="mt-4 d-flex align-items-center gap-3 flex-wrap">
          <button
            class="btn btn-primary"
            :disabled="!allFilled"
            @click="checkAnswers"
          >
            {{
              requirementsConfirmed
                ? "Save Requirements"
                : "Confirm Requirements"
            }}
          </button>

          <!-- Debug: auto-fill correct answers -->
          <button
            v-if="!requirementsConfirmed && isDebugMode"
            class="btn btn-outline-warning btn-sm"
            @click="autoFill"
          >
            <i class="bi bi-lightning-fill me-1" aria-hidden="true"></i>Debug:
            Auto-fill
          </button>

          <output
            v-if="requirementsConfirmed && allCorrect"
            class="text-success small fw-semibold"
            aria-live="polite"
          >
            <i class="bi bi-check-circle-fill me-1" aria-hidden="true"></i>
            Requirements confirmed. You can still edit and save updates.
          </output>

          <output
            v-if="requirementsConfirmed && !allCorrect"
            class="text-warning small fw-semibold"
            aria-live="polite"
          >
            <i
              class="bi bi-exclamation-triangle-fill me-1"
              aria-hidden="true"
            ></i>
            Saved requirements are out of date. Fix highlighted fields and save
            again.
          </output>

          <div
            v-if="submitted && !allCorrect"
            class="text-danger small fw-semibold"
            role="alert"
          >
            <i
              class="bi bi-exclamation-triangle-fill me-1"
              aria-hidden="true"
            ></i
            >Some answers are incorrect — review the highlighted fields and try
            again.
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { M_TO_FT } from "@/utils/units.js";
const ELEVATION_TOLERANCE_FT = 0.1;
const PIPE_LENGTH_TOLERANCE_FT = 0.5;
const EMPTY_ANSWERS = () => ({
  fluidType: "",
  flowRate: "",
  elevation: "",
  pipeRunLength: "",
  budget: "",
  constraints: ""
});

export default {
  name: "ScenarioRequirements",
  props: {
    scenario: {
      type: Object,
      required: true
    },
    fluidOptions: {
      type: Array,
      required: true
    }
  },
  emits: ["requirements-confirmed"],
  data() {
    return {
      answers: EMPTY_ANSWERS(),
      submitted: false
    };
  },
  watch: {
    requirementsAnswers: {
      immediate: true,
      handler(saved) {
        if (!saved) {
          this.answers = EMPTY_ANSWERS();
          this.submitted = false;
          return;
        }

        const hydrated = { ...EMPTY_ANSWERS(), ...saved };
        if (JSON.stringify(this.answers) !== JSON.stringify(hydrated)) {
          this.answers = hydrated;
        }

        if (this.requirementsConfirmed) {
          this.submitted = true;
        }
      }
    }
  },
  computed: {
    ...mapGetters([
      "isDebugMode",
      "requirementsConfirmed",
      "requirementsAnswers"
    ]),
    correctElevation_ft() {
      return parseFloat((this.scenario.elevationChange_m * M_TO_FT).toFixed(1));
    },
    correctPipeRunLength_ft() {
      return parseFloat((this.scenario.pipeRunLength_m * M_TO_FT).toFixed(1));
    },
    validation() {
      return {
        fluidType: this.answers.fluidType === this.scenario.fluidType,
        flowRate:
          Number(this.answers.flowRate) === this.scenario.targetFlowRate_GPM,
        elevation:
          this.answers.elevation !== "" &&
          Math.abs(
            parseFloat(this.answers.elevation) - this.correctElevation_ft
          ) <= ELEVATION_TOLERANCE_FT,
        pipeRunLength:
          this.answers.pipeRunLength !== "" &&
          Math.abs(
            parseFloat(this.answers.pipeRunLength) -
              this.correctPipeRunLength_ft
          ) <= PIPE_LENGTH_TOLERANCE_FT,
        budget: Number(this.answers.budget) === this.scenario.budget,
        constraints: this.answers.constraints === this.scenario.constraints
      };
    },
    allFilled() {
      return (
        this.answers.fluidType !== "" &&
        this.answers.flowRate !== "" &&
        this.answers.elevation !== "" &&
        this.answers.pipeRunLength !== "" &&
        this.answers.budget !== "" &&
        this.answers.constraints !== ""
      );
    },
    allCorrect() {
      return Object.values(this.validation).every(Boolean);
    },
    pipeLengthHint() {
      if (
        this.answers.pipeRunLength === "" ||
        this.answers.pipeRunLength === null
      )
        return null;
      const diff =
        parseFloat(this.answers.pipeRunLength) - this.correctPipeRunLength_ft;
      if (diff < -PIPE_LENGTH_TOLERANCE_FT) return "short";
      if (diff > PIPE_LENGTH_TOLERANCE_FT) return "long";
      return null;
    }
  },
  methods: {
    ...mapActions(["setRequirementsAnswers"]),
    fieldClass(field) {
      if (!this.submitted) return "";
      return this.validation[field] ? "is-valid" : "is-invalid";
    },
    checkAnswers() {
      this.submitted = true;
      this.setRequirementsAnswers({ ...this.answers });
      if (this.allCorrect) {
        this.$emit("requirements-confirmed");
      }
    },
    autoFill() {
      this.answers.fluidType = this.scenario.fluidType;
      this.answers.flowRate = this.scenario.targetFlowRate_GPM;
      this.answers.elevation = this.correctElevation_ft;
      this.answers.pipeRunLength = this.correctPipeRunLength_ft;
      this.answers.budget = this.scenario.budget;
      this.answers.constraints = this.scenario.constraints;
      this.checkAnswers();
    },
    resetForm() {
      this.answers = EMPTY_ANSWERS();
      this.submitted = false;
    }
  }
};
</script>

<style scoped>
.scenario-problem-card {
  border: 1px solid #dbe6f5;
  box-shadow: 0 1px 3px rgba(30, 64, 175, 0.06);
}

.scenario-problem-header {
  background: #f5f8fd;
  border-bottom: 1px solid #dbe6f5;
  border-left: 4px solid #0b5ed7;
}

.scenario-problem-title {
  color: #1f2937;
  font-weight: 700;
}

.scenario-problem-icon {
  color: #0b5ed7;
}

.scenario-problem-body {
  padding-top: 1rem;
  padding-bottom: 1rem;
}

.word-problem {
  font-size: 1rem;
  line-height: 1.75;
  color: #1f2937;
}

.word-problem :deep(strong) {
  color: #0056b3;
  font-weight: 700;
}

.fluid-type-label {
  color: #0b5ed7;
  font-weight: 800;
  letter-spacing: 0.02em;
}
</style>
